'use client';

import { createContext, ReactNode, useContext, useMemo } from 'react';
import { getCopyValue, type CopyOverrides } from '@/contexts/copy/default-copy';

type CopyFunction = (path: string, fallback?: string) => string;

type CopyContextValue = {
  copy: CopyFunction;
  terms: {
    club: {
      singular: string;
      plural: string;
    };
    guide: {
      singular: string;
      plural: string;
    };
    participation: string;
  };
};

const CopyContext = createContext<CopyContextValue | null>(null);

export function CopyProvider({ overrides, children }: { overrides?: CopyOverrides | null; children: ReactNode }) {
  const value = useMemo<CopyContextValue>(() => {
    const copy: CopyFunction = (path, fallback) => getCopyValue(overrides, path, fallback);

    return {
      copy,
      terms: {
        club: {
          singular: copy('common.club.singular'),
          plural: copy('common.club.plural')
        },
        guide: {
          singular: copy('common.guide.singular'),
          plural: copy('common.guide.plural')
        },
        participation: copy('common.participation')
      }
    };
  }, [overrides]);

  return <CopyContext.Provider value={value}>{children}</CopyContext.Provider>;
}

export function useCopy() {
  const context = useContext(CopyContext);

  if (!context) {
    return {
      copy: (path: string, fallback?: string) => getCopyValue(null, path, fallback),
      terms: {
        club: {
          singular: getCopyValue(null, 'common.club.singular'),
          plural: getCopyValue(null, 'common.club.plural')
        },
        guide: {
          singular: getCopyValue(null, 'common.guide.singular'),
          plural: getCopyValue(null, 'common.guide.plural')
        },
        participation: getCopyValue(null, 'common.participation')
      }
    };
  }

  return context;
}
